/**
 * AI Findings queue (AI Reviews tab).
 *
 * Backend: eastview-os run-agent-reviews.md "Loan-level findings queue".
 *   GET  /admin/loans/{dealId}/ai-reviews/findings?status=…      → the queue
 *   POST /admin/loans/{dealId}/ai-reviews/findings/{id}/action   → act on one
 *
 * Candidate findings staged by the QC / hard-exception / suggest-conditions /
 * field-diff agents. Each staged finding can be:
 *   • Promote        → projects an LOS condition (hard-exception findings project
 *                      an EXCEPTION REQUEST instead — the backend decides off
 *                      sourceAgent; the button label reflects it).
 *   • Edit & Promote → same, with an edited title/body (AI-vs-human delta tracked).
 *   • Reject         → requires a reason + a reject category (eval signal).
 *   • Waive          → acknowledged as a valid-but-waived judgment; projects nothing.
 * Promote / Edit & Promote can route the projected item to a user (assignedTo).
 *
 * Admin-only (mounted on the admin-gated AI Review tab). The card is collapsible
 * and hidden entirely when the loan has no findings.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import Collapse from "@mui/material/Collapse";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import RuleOutlined from "@mui/icons-material/RuleOutlined";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import DangerousOutlined from "@mui/icons-material/DangerousOutlined";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import AssignmentOutlined from "@mui/icons-material/AssignmentOutlined";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import OpenInFullRounded from "@mui/icons-material/OpenInFullRounded";
import MoreHorizRounded from "@mui/icons-material/MoreHorizRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { losAiReviewsApi } from "../../../api/losAiReviewsApi";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { filePreviewSetData } from "../../../redux/reducers/filePreviewReducer";
import ExtractTrifoldModal from "../tabs/LoanDrilldownDocumentsTab/extracts/ExtractTrifoldModal";
import { useLoanExtractsIndex } from "../tabs/LoanDrilldownDocumentsTab/extracts/useLoanExtractsIndex";

// Design tokens from the findings spec (:root). Its html font-size is 15px, so
// rem sizes convert at 15px/rem (.8rem → 12px, .67rem → 10.05px, …).
const T = {
  ink: "#171A21",
  ink2: "#4B5563",
  muted: "#8A919E",
  border: "#E4E7EC",
  border2: "#D5D9E0",
  surface: "#FFFFFF",
  surface2: "#FAFBFC",
  accent: "#2E4FD8",
  accentSoft: "#EDF0FD",
  accentInk: "#22399B",
  green: "#0B7A55",
  greenSoft: "#E8F6F0",
  amber: "#9A6700",
  amberSoft: "#FBF3E0",
  red: "#B42318",
  redSoft: "#FBEDEB",
  mono: "ui-monospace, SFMono-Regular, Menlo, monospace",
};

// .sev-icon / .tag tones. Hard exceptions get their own amber tone + label,
// which is why they're a separate tone rather than just a severity.
const SEVERITY = {
  critical: {
    label: "Critical",
    Icon: DangerousOutlined,
    color: T.red,
    bg: T.redSoft,
  },
  warning: {
    label: "Warning",
    Icon: WarningAmberRounded,
    color: T.amber,
    bg: T.amberSoft,
  },
  info: {
    label: "Info",
    Icon: InfoOutlined,
    color: T.muted,
    bg: T.surface2,
  },
  hardex: {
    label: "Hard exception",
    Icon: WarningAmberRounded,
    color: T.amber,
    bg: T.amberSoft,
  },
};

const STATUS_STYLE = {
  staged: { label: "Staged", bg: "#eff6ff", color: "#1d4ed8" },
  promoted: { label: "Promoted", bg: "#ecfdf5", color: "#047857" },
  rejected: { label: "Rejected", bg: "#f1f5f9", color: "#64748b" },
  waived: { label: "Waived", bg: "#f1f5f9", color: "#64748b" },
  superseded: { label: "Superseded", bg: "#f1f5f9", color: "#94a3b8" },
};

// Mirrors RejectCategory in app/models/candidate_findings.py.
const REJECT_CATEGORIES = [
  { value: "not_material", label: "Not material" },
  { value: "already_satisfied", label: "Already satisfied" },
  { value: "duplicate_finding", label: "Duplicate finding" },
  { value: "wrong_reasoning", label: "Wrong reasoning" },
  { value: "bad_ocr_or_structured_output", label: "Bad OCR / extraction" },
  { value: "wrong_doctype_classification", label: "Wrong document type" },
  { value: "bad_wording", label: "Bad wording" },
  { value: "other", label: "Other" },
];

// Findings tabs filter the OPEN (staged) queue by severity. "All Open" first,
// then one tab per severity actually present (critical → warning → info).
const SEV_ORDER = ["critical", "warning", "info"];
const SEV_TAB_LABEL = {
  critical: "Critical",
  warning: "Warnings",
  info: "Info",
};

const prettyAgent = (a) =>
  String(a || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const isHardException = (f) => f?.sourceAgent === "hard_exception_checker";

// Pull a leading category out of the title ("Hard exception: X" → chip + "X").
const parseTitle = (title = "") => {
  const m = String(title).match(/^(Hard exception):\s*(.+)$/i);
  return m ? { category: m[1], text: m[2] } : { category: null, text: title };
};

// Numbers get locale formatting; null / "" → null (an explicit empty marker).
const fmtEvidenceValue = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number")
    return v.toLocaleString("en-US", { maximumFractionDigits: 2 });
  return String(v);
};

// Evidence notes are sometimes JSON-wrapped citations ({ "cite": "…" }).
const parseNote = (note) => {
  if (!note) return null;
  try {
    const parsed = JSON.parse(note);
    if (parsed && typeof parsed === "object") return parsed.cite || null;
  } catch {
    /* plain string */
  }
  return note;
};

// evidence[].blobName is the source file's `file_storage_name` in the {dealId}
// container — the same key the extracts index folders are named after. A couple
// of agents join several blobs with ", " and truncate at 160 chars, so take the
// first entry and ignore whatever got cut off.
const firstBlobName = (blobName) => {
  const first = String(blobName || "").split(",")[0].trim();
  return first || null;
};

const stripExt = (name) => String(name || "").replace(/\.[^.]+$/, "");

// .tag — uppercase severity / hard-exception tag.
const Tag = ({ color, bg, children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      fontSize: "10.05px",
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      padding: "2px 8px",
      borderRadius: "99px",
      color,
      bgcolor: bg,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Box>
);

// .doc-chip — the document / requirement this finding is about. When the source
// document resolved to something viewable, the chip becomes a button that opens
// the trifold viewer (source PDF + structured output side by side).
const DocChip = ({ icon: Icon, children, onClick, title }) => {
  const clickable = typeof onClick === "function";
  return (
    <Box
      component={clickable ? "button" : "span"}
      type={clickable ? "button" : undefined}
      onClick={onClick}
      title={title}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        font: "inherit",
        fontSize: "10.8px",
        fontWeight: 500,
        color: clickable ? T.accentInk : T.ink2,
        bgcolor: clickable ? T.accentSoft : T.surface2,
        border: `1px solid ${clickable ? T.accentSoft : T.border}`,
        borderRadius: "99px",
        padding: "2px 10px",
        whiteSpace: "nowrap",
        cursor: clickable ? "pointer" : "default",
        ...(clickable && {
          "&:hover": { borderColor: T.accent, color: T.accent },
        }),
      }}
    >
      {Icon && <Icon sx={{ fontSize: 12 }} />}
      {children}
      {clickable && <OpenInFullRounded sx={{ fontSize: 10 }} />}
    </Box>
  );
};

// Column-density segmented control (1 / 2 / 3). One column is the default —
// the roomy card; 2 and 3 switch the cards to a compact variant so a long queue
// can be scanned without scrolling past every detail.
const COLUMN_CHOICES = [1, 2, 3];

// Outlined columns glyph: a stroked frame divided into `count` columns. Drawn
// rather than composed from filled bars so it reads as an outline at 1x — bars
// thin enough for three-up leave no visible hollow.
const GLYPH_W = 14;
const GLYPH_H = 11;
const ColumnsGlyph = ({ count }) => {
  const inset = 0.75; // half the stroke width, so the frame sits on the pixel
  const innerW = GLYPH_W - inset * 2;
  const dividers = [];
  for (let i = 1; i < count; i += 1) {
    const x = inset + (innerW / count) * i;
    dividers.push(
      <line key={i} x1={x} y1={inset} x2={x} y2={GLYPH_H - inset} />,
    );
  }
  return (
    <Box
      component="svg"
      width={GLYPH_W}
      height={GLYPH_H}
      viewBox={`0 0 ${GLYPH_W} ${GLYPH_H}`}
      aria-hidden="true"
      sx={{
        display: "block",
        flexShrink: 0,
        fill: "none",
        stroke: "currentColor",
        strokeWidth: 1.25,
      }}
    >
      <rect
        x={inset}
        y={inset}
        width={innerW}
        height={GLYPH_H - inset * 2}
        rx={1.5}
      />
      {dividers}
    </Box>
  );
};

const DensitySelector = ({ value, onChange }) => (
  <Flx
    ac
    sx={{
      gap: "2px",
      p: "3px",
      borderRadius: "8px",
      bgcolor: "#eceef2",
      flexShrink: 0,
    }}
  >
    {COLUMN_CHOICES.map((n) => {
      const active = value === n;
      return (
        <Tooltip
          key={n}
          title={n === 1 ? "Single column" : `${n} columns`}
          disableInteractive
        >
          <Box
            component="button"
            type="button"
            aria-pressed={active}
            onClick={() => onChange(n)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              cursor: "pointer",
              font: "inherit",
              fontSize: "11.5px",
              fontWeight: 600,
              padding: "5px 9px",
              borderRadius: "6px",
              border: "none",
              color: active ? T.ink : T.ink2,
              bgcolor: active ? T.surface : "transparent",
              boxShadow: active ? "0 1px 2px rgba(16,24,40,0.06)" : "none",
              "&:hover": { color: T.ink },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: `${GLYPH_H}px`,
                color: active ? T.accent : T.muted,
              }}
            >
              <ColumnsGlyph count={n} />
            </Box>
            {/* Matching the glyph's box height (rather than relying on the
                text line box) is what keeps the digit optically centered. */}
            <Box
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                height: `${GLYPH_H}px`,
                lineHeight: 1,
              }}
            >
              {n}
            </Box>
          </Box>
        </Tooltip>
      );
    })}
  </Flx>
);

// .status pill for non-staged findings (promoted / rejected / waived).
const StatusPill = ({ bg, color, icon: Icon, children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      fontSize: "10.05px",
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      padding: "2px 8px",
      borderRadius: "99px",
      color,
      bgcolor: bg,
      whiteSpace: "nowrap",
    }}
  >
    {Icon && <Icon sx={{ fontSize: 11 }} />}
    {children}
  </Box>
);

// .check-table — Check / Expected / Computed grid + the source document strip
// and citation. `resolveSource` maps one evidence item to its source document
// (null when the finding has no document behind it, e.g. hard exceptions).
const CheckTable = ({ evidence, resolveSource, onOpenSource, compact }) => {
  if (!Array.isArray(evidence) || evidence.length === 0) return null;
  return (
    <Flx
      column
      sx={{
        gap: 1.25,
        mt: compact ? "10px" : "14px",
        ml: compact ? 0 : "44px",
        maxWidth: compact ? "none" : 760,
      }}
    >
      {evidence.map((ev, i) => {
        const expected = fmtEvidenceValue(ev.expectedValue);
        const computed = fmtEvidenceValue(ev.extractedValue);
        const cite = parseNote(ev.note);
        const source = resolveSource ? resolveSource(ev) : null;
        const cells = [
          { label: "Check", value: ev.extractedField || "—" },
          { label: "Expected", value: expected ?? "—" },
          {
            label: "Computed",
            value: computed ?? "Not available",
            bad: computed === null,
          },
        ];
        return (
          <Box
            key={i}
            sx={{
              border: `1px solid ${T.border}`,
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "grid",
                // Narrow cards stack Check onto its own full-width row so the
                // Expected / Computed pair keeps readable columns.
                gridTemplateColumns: compact
                  ? "1fr 1fr"
                  : { xs: "1fr", sm: "1.3fr 1fr 1fr" },
                bgcolor: T.surface2,
              }}
            >
              {cells.map((c, ci) => (
                <Box
                  key={c.label}
                  sx={{
                    padding: compact ? "8px 12px" : "11px 16px",
                    minWidth: 0,
                    ...(compact &&
                      ci === 0 && {
                        gridColumn: "1 / -1",
                        borderBottom: `1px solid ${T.border}`,
                      }),
                    borderLeft: compact
                      ? ci === 2
                        ? `1px solid ${T.border}`
                        : "none"
                      : {
                          xs: "none",
                          sm: ci === 0 ? "none" : `1px solid ${T.border}`,
                        },
                  }}
                >
                  <Box
                    sx={{
                      fontSize: "9.9px",
                      fontWeight: 600,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      color: T.muted,
                      marginBottom: "3px",
                    }}
                  >
                    {c.label}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: T.mono,
                      fontSize: "12px",
                      color: c.bad ? T.red : T.ink,
                      fontWeight: c.bad ? 500 : 400,
                      wordBreak: "break-word",
                    }}
                  >
                    {c.value}
                  </Box>
                </Box>
              ))}
            </Box>
            {(source || cite) && (
              <Flx
                column
                sx={{
                  gap: "6px",
                  padding: compact ? "8px 12px" : "9px 16px",
                  borderTop: `1px solid ${T.border}`,
                  bgcolor: T.surface,
                }}
              >
                {source && (
                  <Flx ac sx={{ gap: "8px", flexWrap: "wrap", minWidth: 0 }}>
                    <Box
                      component="span"
                      sx={{
                        fontSize: "9.9px",
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        color: T.muted,
                        flexShrink: 0,
                      }}
                    >
                      Source
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => onOpenSource(source, ev)}
                      title={
                        source.kind === "extract"
                          ? "Open the source document and its structured output"
                          : "Open the source document"
                      }
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        minWidth: 0,
                        border: "none",
                        bgcolor: "transparent",
                        p: 0,
                        cursor: "pointer",
                        font: "inherit",
                        fontSize: "11.7px",
                        fontWeight: 600,
                        color: T.accent,
                        textAlign: "left",
                        "&:hover": {
                          color: T.accentInk,
                          textDecoration: "underline",
                        },
                      }}
                    >
                      <DescriptionOutlined
                        sx={{ fontSize: 13, flexShrink: 0 }}
                      />
                      <Box
                        component="span"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {source.name}
                      </Box>
                      <OpenInFullRounded sx={{ fontSize: 11, flexShrink: 0 }} />
                    </Box>
                    {source.docType && (
                      <Box
                        component="span"
                        sx={{ fontSize: "11.25px", color: T.muted }}
                      >
                        · {source.docType}
                      </Box>
                    )}
                  </Flx>
                )}
                {cite && (
                  <Box
                    title={compact ? cite : undefined}
                    sx={{
                      fontSize: "11.25px",
                      color: T.muted,
                      lineHeight: 1.5,
                      ...(compact && {
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }),
                    }}
                  >
                    {cite}
                  </Box>
                )}
              </Flx>
            )}
          </Box>
        );
      })}
    </Flx>
  );
};

// One finding.
const FindingCard = ({
  finding,
  busy,
  onPromote,
  onEdit,
  onReject,
  onWaive,
  onRestore,
  resolveSource,
  onOpenSource,
  compact,
}) => {
  const [showReasoning, setShowReasoning] = useState(false);
  // Compact cards keep Promote inline and fold the rest into an overflow menu —
  // four buttons don't fit a 2/3-column card without wrapping.
  const [menuAnchor, setMenuAnchor] = useState(null);
  // The 44px gutter under the severity icon reads well full-width but wastes a
  // third of a narrow card, so compact content runs flush.
  const indent = compact ? 0 : "44px";
  const hardEx = isHardException(finding);
  // Hard exceptions get their own amber tone/label; everything else is severity.
  const tone = hardEx
    ? SEVERITY.hardex
    : SEVERITY[finding.severity] || SEVERITY.info;
  const st = STATUS_STYLE[finding.status] || STATUS_STYLE.staged;
  const ToneIcon = tone.Icon;
  const { text } = parseTitle(finding.title);
  const actionable = finding.status === "staged";
  const isDoc = finding.requirementType === "Document";
  // The first evidence item that resolved to a real document — drives the head
  // chip so the whole finding is one click from its source.
  const primary = useMemo(() => {
    for (const ev of finding.evidence || []) {
      const s = resolveSource?.(ev);
      if (s) return { source: s, ev };
    }
    return null;
  }, [finding.evidence, resolveSource]);
  const sourceParts = [
    finding.authorityToWaive
      ? `Waivable by ${finding.authorityToWaive}`
      : "Not waivable",
    formatDateTime(finding.createdAt),
  ].filter(Boolean);

  const iconBubble = (
    <Box
      sx={{
        mt: "1px",
        width: compact ? 26 : 30,
        height: compact ? 26 : 30,
        borderRadius: "99px",
        bgcolor: tone.bg,
        color: tone.color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <ToneIcon sx={{ fontSize: compact ? 14 : 16 }} />
    </Box>
  );

  const titleEl = (
    <Box
      component="h3"
      sx={{
        m: 0,
        fontSize: compact ? "13.5px" : "15px",
        fontWeight: compact ? 700 : 600,
        letterSpacing: "-0.012em",
        color: T.ink,
        lineHeight: 1.4,
      }}
    >
      {text}
    </Box>
  );

  const badges = (
    <>
      <Tag color={tone.color} bg={tone.bg}>
        {tone.label}
      </Tag>
      {(finding.docType || finding.requirementType) && (
        <DocChip
          icon={isDoc ? DescriptionOutlined : AssignmentOutlined}
          onClick={
            primary ? () => onOpenSource(primary.source, primary.ev) : undefined
          }
          title={primary ? `Open ${primary.source.name}` : undefined}
        >
          {finding.docType || finding.requirementType}
        </DocChip>
      )}
      {!actionable && (
        <StatusPill
          bg={st.bg}
          color={st.color}
          icon={finding.status === "promoted" ? CheckCircleRounded : undefined}
        >
          {st.label}
        </StatusPill>
      )}
    </>
  );

  const sourceLine = (
    <Box
      sx={{ fontSize: "11.25px", color: T.muted, mt: compact ? 0 : "3px" }}
    >
      <Box component="span" sx={{ color: T.ink2, fontWeight: 600 }}>
        {prettyAgent(finding.sourceAgent)}
      </Box>
      {sourceParts.length > 0 && ` · ${sourceParts.join(" · ")}`}
    </Box>
  );

  return (
    <Box
      sx={{
        bgcolor: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: compact ? "12px" : "14px",
        padding: compact ? "15px 16px" : "22px 26px",
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
        transition: "box-shadow 0.15s ease",
        opacity: actionable ? 1 : 0.68,
        // Cards in a grid row stretch to the tallest sibling; the column layout
        // pins the actions to the bottom so the rows line up.
        ...(compact && { display: "flex", flexDirection: "column" }),
        "&:hover": { boxShadow: "0 2px 8px rgba(16,24,40,0.07)" },
      }}
    >
      {/* .f-head — full width keeps the title inline with its chips; a narrow
          card can't fit both, so the icon + chips get their own row above the
          title rather than squeezing it into a sliver. */}
      {compact ? (
        <Flx column sx={{ gap: "7px" }}>
          <Flx ac sx={{ gap: "7px", flexWrap: "wrap" }}>
            {iconBubble}
            {badges}
          </Flx>
          {titleEl}
          {sourceLine}
        </Flx>
      ) : (
        <Flx sx={{ gap: "14px", alignItems: "flex-start" }}>
          {iconBubble}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {/* .f-title-row */}
            <Flx ac sx={{ gap: "10px", flexWrap: "wrap" }}>
              {titleEl}
              {badges}
            </Flx>
            {sourceLine}
          </Box>
        </Flx>
      )}

      {/* .f-body */}
      {finding.description && (
        <Box
          title={compact ? finding.description : undefined}
          sx={{
            fontSize: compact ? "12.2px" : "12.9px",
            color: T.ink2,
            lineHeight: 1.55,
            mt: compact ? "10px" : "14px",
            ml: indent,
            // Clamped in compact so one long description can't stretch a whole
            // grid row; the full text is in the tooltip.
            ...(compact && {
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }),
          }}
        >
          {finding.description}
        </Box>
      )}

      <CheckTable
        evidence={finding.evidence}
        resolveSource={resolveSource}
        onOpenSource={onOpenSource}
        compact={compact}
      />

      {/* .why */}
      {finding.reasoning && (
        <Box sx={{ mt: "12px", ml: indent }}>
          <Box
            component="button"
            type="button"
            onClick={() => setShowReasoning((v) => !v)}
            sx={{
              border: "none",
              bgcolor: "transparent",
              cursor: "pointer",
              p: "2px 0",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "11.7px",
              fontWeight: 600,
              color: T.ink2,
              "&:hover": { color: T.accent },
            }}
          >
            <ChevronRightRounded
              sx={{
                fontSize: 14,
                color: T.muted,
                transition: "transform 0.15s ease",
                transform: showReasoning ? "rotate(90deg)" : "none",
              }}
            />
            Why this was flagged
          </Box>
          <Collapse in={showReasoning} unmountOnExit>
            <Box
              sx={{
                mt: "8px",
                fontSize: "12px",
                color: T.ink2,
                bgcolor: T.surface2,
                border: `1px solid ${T.border}`,
                borderRadius: "9px",
                padding: "11px 14px",
                lineHeight: 1.6,
                maxWidth: compact ? "none" : 760,
                whiteSpace: "pre-wrap",
              }}
            >
              {finding.reasoning}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* .f-actions */}
      {actionable ? (
        <Flx
          ac
          sx={{
            gap: compact ? "6px" : "10px",
            flexWrap: "wrap",
            // "auto" pins the actions to the bottom of a stretched grid card so
            // rows of cards line up regardless of body length.
            mt: compact ? "auto" : "18px",
            pt: compact ? "14px" : 0,
            ml: indent,
          }}
        >
          <Button
            size="small"
            disableElevation
            disabled={busy}
            onClick={() => onPromote(finding)}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 600,
              padding: compact ? "6px 12px" : "8px 16px",
              borderRadius: "8px",
              bgcolor: T.accent,
              color: "#fff",
              "&:hover": { bgcolor: T.accentInk, color: "#fff" },
            }}
          >
            {compact
              ? "Promote"
              : hardEx
                ? "Promote to exception"
                : "Promote to condition"}
          </Button>

          {compact ? (
            <>
              {finding.authorityToWaive && (
                <Button
                  size="small"
                  disabled={busy}
                  onClick={() => onWaive(finding)}
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    fontWeight: 600,
                    padding: "6px 10px",
                    borderRadius: "8px",
                    color: T.ink2,
                    bgcolor: "transparent",
                    "&:hover": { bgcolor: T.surface2, color: T.ink },
                  }}
                >
                  Waive
                </Button>
              )}
              <Box sx={{ ml: "auto" }}>
                <IconButton
                  size="small"
                  disabled={busy}
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  aria-label="More actions"
                  sx={{
                    border: `1px solid ${T.border}`,
                    borderRadius: "8px",
                    color: T.muted,
                    "&:hover": { borderColor: T.border2, color: T.ink },
                  }}
                >
                  <MoreHorizRounded sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              <Menu
                anchorEl={menuAnchor}
                open={!!menuAnchor}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem
                  sx={{ fontSize: 12.5 }}
                  onClick={() => {
                    setMenuAnchor(null);
                    onEdit(finding);
                  }}
                >
                  Edit and promote
                </MenuItem>
                <MenuItem
                  sx={{ fontSize: 12.5, color: T.red }}
                  onClick={() => {
                    setMenuAnchor(null);
                    onReject(finding);
                  }}
                >
                  Reject
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Button
                size="small"
                disabled={busy}
                onClick={() => onEdit(finding)}
                sx={{
                  textTransform: "none",
                  fontSize: "12px",
                  fontWeight: 600,
                  padding: "8px 16px",
                  borderRadius: "8px",
                  bgcolor: T.surface,
                  color: T.ink,
                  border: `1px solid ${T.border2}`,
                  "&:hover": { bgcolor: T.surface2, color: T.ink },
                }}
              >
                Edit and promote
              </Button>
              <Box sx={{ width: "1px", height: 20, bgcolor: T.border }} />
              {finding.authorityToWaive && (
                <Button
                  size="small"
                  disabled={busy}
                  onClick={() => onWaive(finding)}
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    fontWeight: 600,
                    padding: "8px 10px",
                    borderRadius: "8px",
                    color: T.ink2,
                    bgcolor: "transparent",
                    "&:hover": { bgcolor: T.surface2, color: T.ink },
                  }}
                >
                  Waive
                </Button>
              )}
              <Button
                size="small"
                disabled={busy}
                onClick={() => onReject(finding)}
                sx={{
                  textTransform: "none",
                  fontSize: "12px",
                  fontWeight: 600,
                  padding: "8px 10px",
                  borderRadius: "8px",
                  color: T.ink2,
                  bgcolor: "transparent",
                  "&:hover": { color: T.red, bgcolor: T.redSoft },
                }}
              >
                Reject
              </Button>
            </>
          )}
          {busy && (
            <CircularProgress size={14} thickness={5} sx={{ ml: 0.5 }} />
          )}
        </Flx>
      ) : finding.status === "promoted" ? (
        // .resolved.promoted
        <Flx
          ac
          sx={{
            display: "inline-flex",
            gap: "8px",
            mt: compact ? "auto" : "16px",
            ml: indent,
            ...(compact && { alignSelf: "flex-start" }),
            padding: "8px 14px",
            borderRadius: "8px",
            fontSize: "12.3px",
            color: T.green,
            bgcolor: T.greenSoft,
          }}
        >
          <CheckCircleRounded sx={{ fontSize: 15 }} />
          Promoted to {hardEx ? "an exception request" : "a condition"}
        </Flx>
      ) : finding.status === "rejected" || finding.status === "waived" ? (
        // Rejected / waived — the decision is reversible via Restore (returns the
        // finding to the staged queue).
        <Flx
          ac
          sx={{
            gap: "10px",
            flexWrap: "wrap",
            mt: compact ? "auto" : "16px",
            ml: indent,
          }}
        >
          <Box component="span" sx={{ fontSize: "12px", color: T.muted }}>
            {finding.status === "rejected" ? "Rejected" : "Waived"}
            {finding.resolutionReason ? ` — ${finding.resolutionReason}` : ""}
          </Box>
          <Button
            size="small"
            disabled={busy}
            onClick={() => onRestore(finding)}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 600,
              padding: "6px 12px",
              borderRadius: "8px",
              bgcolor: T.surface,
              color: T.ink,
              border: `1px solid ${T.border2}`,
              "&:hover": { bgcolor: T.surface2, color: T.ink },
            }}
          >
            Restore
          </Button>
          {busy && <CircularProgress size={13} thickness={5} />}
        </Flx>
      ) : null}
    </Box>
  );
};

const AiFindingsPanel = ({
  onCount,
  scope = "all",
  title,
  subtitle,
  onActioned,
  warningTint = false,
  defaultExpanded = true,
  hideWhenEmpty = false,
  hideIcon = false,
  adminChip = false,
  rootSx = null,
  // When supplied, an "Ignore" button sits next to Refresh. The caller owns
  // what ignoring means (e.g. a persisted per-user preference).
  onIgnore = null,
  ignoreLabel = "Ignore",
  ignoreTooltip = "",
  ignoreIcon = null,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const users = useSelector((state) => state.users);
  const dealId = loan?._id;
  const loanDocuments = loan?.loanDocuments;

  // The extracts index — same feed the Documents tab's trifold viewer uses. It
  // is what lets an evidence `blobName` become a viewable document + structured
  // output rather than an opaque string.
  const { documents: extractDocs } = useLoanExtractsIndex(dealId);

  // { selectedId (parent_document_id), focusField } — drives the trifold modal.
  const [viewer, setViewer] = useState(null);

  const [findings, setFindings] = useState(null); // null = not loaded
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [filter, setFilter] = useState("all"); // "all" (open) | a severity key
  const [columns, setColumns] = useState(1); // grid density: 1 | 2 | 3
  const compact = columns > 1;
  const actioningRef = useRef(new Set()); // candidateIds mid-action
  const [busyIds, setBusyIds] = useState([]); // mirror for re-render

  // Unified promote dialog (plain promote + edit-and-promote share it): carries
  // an optional assignee so the projected condition/exception can be routed.
  const [promoteDialog, setPromoteDialog] = useState(null); // { finding, edit, title, body, assignee }
  const [rejectDialog, setRejectDialog] = useState(null); // { finding, reason, category }

  // Assignee options for the promote picker.
  const userOptions = useMemo(
    () =>
      (Array.isArray(users) ? users : [])
        .filter((u) => u?.emailAddress)
        .map((u) => ({
          email: u.emailAddress,
          label:
            `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.emailAddress,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users],
  );

  // blobName → extracts-index entry. Both the storage name and its
  // extension-stripped stem are indexed because the backend joins the same two
  // ways; parent ids are indexed too for the one agent that emits a document
  // oid in blobName instead of a storage name.
  const extractIndex = useMemo(() => {
    const map = new Map();
    const put = (k, v) => {
      const key = String(k || "").trim();
      if (key && !map.has(key)) map.set(key, v);
    };
    (extractDocs || []).forEach((d) => {
      const p = d?.parent || {};
      put(p.file_storage_name, d);
      put(stripExt(p.file_storage_name), d);
      put(d?.parent_document_id, d);
      put(p._id, d);
    });
    return map;
  }, [extractDocs]);

  // Same lookup against the loan's own documents — the fallback for a source
  // that was never parsed (no extracts), which still opens in the file-preview
  // sidebar even though there's no structured output to show beside it.
  const loanDocIndex = useMemo(() => {
    const map = new Map();
    const put = (k, v) => {
      const key = String(k || "").trim();
      if (key && !map.has(key)) map.set(key, v);
    };
    (Array.isArray(loanDocuments) ? loanDocuments : []).forEach((d) => {
      put(d?.file_storage_name, d);
      put(stripExt(d?.file_storage_name), d);
      put(d?._id, d);
    });
    return map;
  }, [loanDocuments]);

  // One evidence item → the document behind it, or null when there is none
  // (hard-exception findings check loan/sizer fields, not documents) or when
  // the referenced blob is no longer on the loan.
  const resolveSource = useCallback(
    (ev) => {
      const blob = firstBlobName(ev?.blobName);
      if (!blob) return null;
      const stem = stripExt(blob);

      const entry = extractIndex.get(blob) || extractIndex.get(stem);
      if (entry?.parent_document_id) {
        const p = entry.parent || entry.parent_provenance || {};
        return {
          kind: "extract",
          selectedId: entry.parent_document_id,
          name: p.file_display_name || p.name || blob,
          docType: ev?.sourceDocType || p.docType || null,
        };
      }

      const doc = loanDocIndex.get(blob) || loanDocIndex.get(stem);
      if (doc) {
        return {
          kind: "doc",
          doc,
          name: doc.file_display_name || doc.file_name_as_loaded || blob,
          docType: ev?.sourceDocType || doc.docType || null,
        };
      }
      return null;
    },
    [extractIndex, loanDocIndex],
  );

  // Parsed documents open in the trifold viewer (source · fields · text/JSON),
  // landing on the exact field the finding cited; unparsed ones fall back to
  // the plain file-preview sidebar.
  const openSource = useCallback(
    (source, ev) => {
      if (!source) return;
      if (source.kind === "extract") {
        setViewer({
          selectedId: source.selectedId,
          focusField: ev?.extractedField || null,
        });
        return;
      }
      dispatch(filePreviewSetData(source.doc));
    },
    [dispatch],
  );

  const fetchFindings = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      // Fetch all statuses; filter client-side so the counts are accurate.
      const data = await losAiReviewsApi.getFindings(dealId);
      setFindings(Array.isArray(data) ? data : []);
    } catch {
      setFindings([]);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    fetchFindings();
  }, [fetchFindings]);

  const applyAction = useCallback(
    async (finding, payload) => {
      const id = finding?.candidateId;
      if (!dealId || !id || actioningRef.current.has(id)) return;
      actioningRef.current.add(id);
      setBusyIds((b) => [...b, id]);
      try {
        const res = await losAiReviewsApi.actionFinding(dealId, id, payload);
        const created = res?.losExceptionId
          ? "exception request"
          : res?.losConditionId
            ? "condition"
            : null;
        let msg;
        if (payload.action === "reject") msg = "Finding rejected.";
        else if (payload.action === "acknowledge_waive")
          msg = "Finding waived.";
        else if (payload.action === "restore")
          msg = "Finding restored to the queue.";
        else
          msg = created
            ? `Finding promoted — ${created} created.`
            : "Finding promoted.";
        enqueueSnackbar(msg, { variant: "success" });
        fetchFindings();
        // Let an embedding surface (e.g. the Conditions checklist) refresh when a
        // finding projects a real condition/exception onto the loan.
        if (created) onActioned?.(created);
      } catch (err) {
        enqueueSnackbar(
          err?.response?.data?.detail || err?.message || "Action failed.",
          { variant: "error" },
        );
      } finally {
        actioningRef.current.delete(id);
        setBusyIds((b) => b.filter((x) => x !== id));
      }
    },
    [dealId, enqueueSnackbar, fetchFindings, onActioned],
  );

  const openPromote = useCallback(
    (finding, edit) =>
      setPromoteDialog({
        finding,
        edit,
        title: finding.title || "",
        body: finding.description || "",
        assignee: null,
      }),
    [],
  );

  const submitPromote = useCallback(() => {
    if (!promoteDialog) return;
    const { finding, edit, title, body, assignee } = promoteDialog;
    setPromoteDialog(null);
    const payload = { action: edit ? "edit_promote" : "promote" };
    if (edit) {
      payload.editedTitle = title;
      payload.editedBody = body;
    }
    if (assignee?.email) payload.assignedTo = assignee.email;
    applyAction(finding, payload);
  }, [promoteDialog, applyAction]);

  const submitReject = useCallback(() => {
    if (!rejectDialog) return;
    const { finding, reason, category } = rejectDialog;
    if (!reason?.trim() || !category) return;
    setRejectDialog(null);
    applyAction(finding, {
      action: "reject",
      reason: reason.trim(),
      rejectCategory: category,
    });
  }, [rejectDialog, applyAction]);

  // Optional category scope — this instance shows only hard-exception findings,
  // only condition-suggestion findings, or all of them, so the AI Review tab can
  // mount one panel per section (Hard Exceptions / Suggested Conditions).
  const scopedFindings = useMemo(() => {
    const all = findings || [];
    if (scope === "hard") return all.filter(isHardException);
    if (scope === "conditions") return all.filter((f) => !isHardException(f));
    return all;
  }, [findings, scope]);

  const total = scopedFindings.length;
  // The open (staged) queue — the tabs slice this by severity.
  const openFindings = useMemo(
    () => scopedFindings.filter((f) => f.status === "staged"),
    [scopedFindings],
  );
  const stagedCount = openFindings.length;

  // Resolved (rejected / waived) findings — kept visible so a decision can be
  // reversed. `restore` returns one to the staged queue (backend action 'restore').
  const resolvedFindings = useMemo(
    () =>
      scopedFindings.filter(
        (f) => f.status === "rejected" || f.status === "waived",
      ),
    [scopedFindings],
  );

  // Report the open count up to the section-tab badge.
  useEffect(() => {
    onCount?.(stagedCount);
  }, [onCount, stagedCount]);

  // Counts per severity within the open queue.
  const sevCounts = useMemo(() => {
    const c = {};
    for (const f of openFindings) c[f.severity] = (c[f.severity] || 0) + 1;
    return c;
  }, [openFindings]);

  // Hard exceptions are their own tab (they project exception requests rather
  // than conditions), sitting between Critical and Warnings per the spec.
  const hardExCount = useMemo(
    () => openFindings.filter(isHardException).length,
    [openFindings],
  );

  // Count for whichever tab key — severities plus the hard-exception cut.
  const countFor = useCallback(
    (key) =>
      key === "all"
        ? openFindings.length
        : key === "resolved"
          ? resolvedFindings.length
          : key === "hardex"
            ? hardExCount
            : sevCounts[key] || 0,
    [openFindings.length, resolvedFindings.length, hardExCount, sevCounts],
  );

  // "All open" + Critical + Hard exceptions + Warnings/Info — only what's present.
  const filters = useMemo(() => {
    const list = [{ key: "all", label: "All open" }];
    if (sevCounts.critical)
      list.push({ key: "critical", label: SEV_TAB_LABEL.critical });
    // In the Hard Exceptions section every finding is already hard, so the
    // hard-exceptions sub-filter would be redundant — only offer it in "all".
    if (hardExCount && scope !== "hard")
      list.push({ key: "hardex", label: "Hard exceptions" });
    for (const s of SEV_ORDER) {
      if (s !== "critical" && sevCounts[s])
        list.push({ key: s, label: SEV_TAB_LABEL[s] });
    }
    // Resolved (rejected/waived) sit last — a review-and-restore surface.
    if (resolvedFindings.length)
      list.push({ key: "resolved", label: "Rejected / Waived" });
    return list.map((f) => ({ ...f, count: countFor(f.key) }));
  }, [sevCounts, hardExCount, resolvedFindings.length, countFor]);

  // If the active tab emptied out (e.g. last critical resolved), fall back to
  // All open so we never sit on a tab that no longer exists.
  useEffect(() => {
    if (filter !== "all" && !countFor(filter)) setFilter("all");
  }, [filter, countFor]);

  const shown = useMemo(() => {
    if (filter === "all") return openFindings;
    if (filter === "resolved") return resolvedFindings;
    if (filter === "hardex") return openFindings.filter(isHardException);
    return openFindings.filter((f) => f.severity === filter);
  }, [openFindings, resolvedFindings, filter]);

  // Standalone: hide the whole card when the loan has no findings. Scoped into a
  // section tab: keep the card (the tab still needs its header + empty state).
  // `hideWhenEmpty` (the Conditions / Exceptions tabs) hides it in any scope
  // when nothing is OPEN — rejected / waived findings alone don't keep a
  // zero-count card on those tabs; they stay reviewable from the AI Review tab.
  if (total === 0 && scope === "all") return null;
  if (hideWhenEmpty && stagedCount === 0) return null;

  return (
    <Box
      sx={{
        bgcolor: warningTint ? "#fffdf5" : "#fff",
        border: `1px solid ${warningTint ? "#fde68a" : "#e5e8ee"}`,
        borderRadius: "14px",
        boxShadow:
          "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.08)",
        overflow: "hidden",
        ...rootSx,
      }}
    >
      {/* Header — collapse toggle */}
      <Flx
        ac
        onClick={() => setExpanded((v) => !v)}
        sx={{
          gap: 1.5,
          p: "18px 24px",
          cursor: "pointer",
          userSelect: "none",
          borderBottom: expanded ? "1px solid #eef1f5" : "none",
          "&:hover": { bgcolor: "#fafbfc" },
        }}
      >
        {!hideIcon && (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "9px",
              background: "linear-gradient(135deg, #f5f3ff, #ede9fe)",
              border: "1px solid #e6e0fb",
              color: "#7c3aed",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <RuleOutlined sx={{ fontSize: 24 }} />
          </Box>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Flx ac sx={{ gap: 1 }}>
            <Txt
              sx={{
                fontSize: 15,
                fontWeight: 700,
                letterSpacing: "-0.01em",
                color: "#0f172a",
              }}
            >
              {title || "AI Findings"}
            </Txt>
            {stagedCount > 0 && (
              <Chip
                size="small"
                label={`${stagedCount} open`}
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 600,
                  // Soft amber — subtle, but still separates from BOTH the white
                  // card and the yellow-tinted Conditions-tab card.
                  bgcolor: "#fde68a",
                  color: "#92400e",
                }}
              />
            )}
            {adminChip && (
              <Tooltip
                arrow
                placement="bottom"
                title="You can see this because your account has admin access. Teammates without it won't."
              >
                <Chip
                  label="Admin only"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 10,
                    fontWeight: 600,
                    background: "#f3e8ff",
                    color: "#5b21b6",
                    cursor: "help",
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              </Tooltip>
            )}
          </Flx>
          <Txt
            sx={{
              fontSize: 12.5,
              color: "#64748b",
              lineHeight: 1.5,
              display: "block",
            }}
          >
            {subtitle ||
              "Candidate conditions and hard exceptions the agents staged. Promote to project a condition (or exception), or reject / waive."}
          </Txt>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            fetchFindings();
          }}
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={13} thickness={5} sx={{ color: "inherit" }} />
            ) : (
              <RefreshRounded sx={{ fontSize: "16px !important" }} />
            )
          }
          sx={{
            textTransform: "none",
            flexShrink: 0,
            fontSize: 12,
            fontWeight: 600,
            borderRadius: "8px",
            px: 1.25,
            py: 0.375,
            color: "#475569",
            borderColor: "#dbe1ea",
            bgcolor: "#fff",
            "& .MuiButton-startIcon": { mr: 0.5 },
            "&:hover": {
              borderColor: "#c3ccd8",
              bgcolor: "#f8fafc",
              color: "#0f172a",
            },
            "&.Mui-disabled": { color: "#94a3b8", borderColor: "#e5e8ee" },
          }}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
        {onIgnore && (
          <Tooltip title={ignoreTooltip} arrow disableHoverListener={!ignoreTooltip}>
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onIgnore();
              }}
              startIcon={
                ignoreIcon || (
                  <VisibilityOffOutlined sx={{ fontSize: "16px !important" }} />
                )
              }
              sx={{
                textTransform: "none",
                flexShrink: 0,
                fontSize: 12,
                fontWeight: 600,
                borderRadius: "8px",
                px: 1.25,
                py: 0.375,
                color: "#475569",
                borderColor: "#dbe1ea",
                bgcolor: "#fff",
                "& .MuiButton-startIcon": { mr: 0.5 },
                "&:hover": {
                  borderColor: "#c3ccd8",
                  bgcolor: "#f8fafc",
                  color: "#0f172a",
                },
              }}
            >
              {ignoreLabel}
            </Button>
          </Tooltip>
        )}
        <ExpandMoreRounded
          sx={{
            fontSize: 22,
            color: "#94a3b8",
            flexShrink: 0,
            transition: "transform 0.15s ease",
            transform: expanded ? "rotate(180deg)" : "none",
          }}
        />
      </Flx>

      {/* Body — gray canvas with the filter pill sitting on it. */}
      <Collapse in={expanded} unmountOnExit>
        <Box
          sx={{ p: "16px 24px 20px", bgcolor: warningTint ? "#fffdf5" : "#f8fafc" }}
        >
          {/* .filters — standalone rounded chips, active = solid ink. The
              density selector rides on the same row, pushed right. */}
          <Flx ac wrap sx={{ gap: "8px", mb: "20px" }}>
            {filters.map((f) => {
              const active = filter === f.key;
              return (
                <Box
                  key={f.key}
                  component="button"
                  type="button"
                  onClick={() => setFilter(f.key)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "7px",
                    cursor: "pointer",
                    font: "inherit",
                    fontSize: "12px",
                    fontWeight: active ? 600 : 500,
                    padding: "6px 15px",
                    borderRadius: "99px",
                    whiteSpace: "nowrap",
                    color: active ? "#fff" : T.ink2,
                    bgcolor: active ? T.ink : T.surface,
                    border: `1px solid ${active ? T.ink : T.border2}`,
                    "&:hover": {
                      bgcolor: active ? T.ink : T.surface,
                      color: active ? "#fff" : T.ink2,
                      borderColor: active ? T.ink : T.muted,
                    },
                  }}
                >
                  {f.label}
                  <Box
                    component="span"
                    sx={{
                      fontFamily: T.mono,
                      fontSize: "10.2px",
                      opacity: 0.7,
                    }}
                  >
                    {f.count}
                  </Box>
                </Box>
              );
            })}
            <Box sx={{ ml: "auto" }}>
              <DensitySelector value={columns} onChange={setColumns} />
            </Box>
          </Flx>

          {/* Findings */}
          {shown.length === 0 ? (
            <Flx column ac jc sx={{ py: 5, px: 3, gap: 0.5 }}>
              <CheckCircleRounded sx={{ fontSize: 26, color: "#059669" }} />
              <Txt sx={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a" }}>
                {filter === "all"
                  ? "No open findings"
                  : filter === "hardex"
                    ? "No open hard exceptions"
                    : `No open ${SEV_TAB_LABEL[filter]?.toLowerCase() || ""} findings`}
              </Txt>
              <Txt
                sx={{ fontSize: 12.5, color: "#94a3b8", textAlign: "center" }}
              >
                {filter === "all"
                  ? "Everything staged has been reviewed."
                  : "Nothing open in this category."}
              </Txt>
            </Flx>
          ) : (
            <Box
              sx={{
                display: "grid",
                gap: compact ? "14px" : "16px",
                alignItems: "stretch",
                // Step down a column at a time on narrower viewports rather than
                // squeezing three cards into a phone width.
                gridTemplateColumns: {
                  xs: "1fr",
                  sm:
                    columns >= 2 ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)",
                  lg: `repeat(${columns}, minmax(0, 1fr))`,
                },
              }}
            >
              {shown.map((f) => (
                <FindingCard
                  key={f.candidateId}
                  compact={compact}
                  finding={f}
                  busy={busyIds.includes(f.candidateId)}
                  onPromote={(fi) => openPromote(fi, false)}
                  onWaive={(fi) =>
                    applyAction(fi, { action: "acknowledge_waive" })
                  }
                  onEdit={(fi) => openPromote(fi, true)}
                  onReject={(fi) =>
                    setRejectDialog({ finding: fi, reason: "", category: "" })
                  }
                  onRestore={(fi) => applyAction(fi, { action: "restore" })}
                  resolveSource={resolveSource}
                  onOpenSource={openSource}
                />
              ))}
            </Box>
          )}
        </Box>
      </Collapse>

      {/* Source document viewer — the same trifold surface the Documents tab
          uses, so a finding's evidence is read against the actual document and
          its structured output. Navigating to another doc clears the field
          highlight, which only applies to the document the finding cited. */}
      <ExtractTrifoldModal
        open={!!viewer}
        dealId={dealId}
        documents={extractDocs}
        selectedId={viewer?.selectedId}
        onSelectId={(id) => setViewer({ selectedId: id, focusField: null })}
        onClose={() => setViewer(null)}
        focusField={viewer?.focusField}
      />

      {/* Promote / Edit & Promote dialog (shared) */}
      <Dialog
        open={!!promoteDialog}
        onClose={() => setPromoteDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600 }}>
          {promoteDialog?.edit
            ? "Edit & Promote"
            : isHardException(promoteDialog?.finding)
              ? "Promote to Exception"
              : "Promote to Condition"}
        </DialogTitle>
        <DialogContent>
          <Flx column sx={{ gap: 2, pt: 1 }}>
            {promoteDialog?.edit && (
              <>
                <TextField
                  size="small"
                  label={
                    isHardException(promoteDialog?.finding)
                      ? "Exception title"
                      : "Condition title"
                  }
                  value={promoteDialog?.title || ""}
                  onChange={(e) =>
                    setPromoteDialog((d) => ({ ...d, title: e.target.value }))
                  }
                  InputProps={{ sx: { fontSize: 13 } }}
                  InputLabelProps={{ sx: { fontSize: 13 } }}
                />
                <TextField
                  size="small"
                  label={
                    isHardException(promoteDialog?.finding)
                      ? "Exception body"
                      : "Condition body"
                  }
                  multiline
                  minRows={3}
                  value={promoteDialog?.body || ""}
                  onChange={(e) =>
                    setPromoteDialog((d) => ({ ...d, body: e.target.value }))
                  }
                  InputProps={{ sx: { fontSize: 13 } }}
                  InputLabelProps={{ sx: { fontSize: 13 } }}
                />
              </>
            )}
            <Autocomplete
              size="small"
              options={userOptions}
              value={promoteDialog?.assignee || null}
              onChange={(_, v) =>
                setPromoteDialog((d) => ({ ...d, assignee: v }))
              }
              getOptionLabel={(o) => o?.label || ""}
              isOptionEqualToValue={(o, v) => o.email === v?.email}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign to (optional)"
                  helperText={
                    promoteDialog?.finding?.assignableTo
                      ? `Suggested: ${promoteDialog.finding.assignableTo}`
                      : "Route the projected item to a specific person"
                  }
                  InputProps={{ ...params.InputProps, sx: { fontSize: 13 } }}
                  InputLabelProps={{ sx: { fontSize: 13 } }}
                />
              )}
            />
          </Flx>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setPromoteDialog(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={submitPromote}
            disabled={promoteDialog?.edit && !promoteDialog?.title?.trim()}
          >
            {promoteDialog?.edit ? "Promote" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog */}
      <Dialog
        open={!!rejectDialog}
        onClose={() => setRejectDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600 }}>
          Reject finding
        </DialogTitle>
        <DialogContent>
          <Flx column sx={{ gap: 2, pt: 1 }}>
            <TextField
              select
              size="small"
              label="Category"
              value={rejectDialog?.category || ""}
              onChange={(e) =>
                setRejectDialog((d) => ({ ...d, category: e.target.value }))
              }
              InputProps={{ sx: { fontSize: 13 } }}
              InputLabelProps={{ sx: { fontSize: 13 } }}
            >
              {REJECT_CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value} sx={{ fontSize: 13 }}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Reason"
              multiline
              minRows={2}
              value={rejectDialog?.reason || ""}
              onChange={(e) =>
                setRejectDialog((d) => ({ ...d, reason: e.target.value }))
              }
              InputProps={{ sx: { fontSize: 13 } }}
              InputLabelProps={{ sx: { fontSize: 13 } }}
            />
          </Flx>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setRejectDialog(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disableElevation
            onClick={submitReject}
            disabled={!rejectDialog?.reason?.trim() || !rejectDialog?.category}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AiFindingsPanel;
